import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateCargoProblemWorkspaceInputSchema,
  type CargoProblemActor,
  type CargoProblemCategory,
  type CargoProblemDetail,
  type CargoProblemQueue,
  type CreateCargoProblemWorkspaceInput,
} from '@magimp/shared';
import {
  Banner,
  Button,
  Combobox,
  EditorDrawer,
  FormRow,
  SectionCard,
  Select,
  Textarea,
  useNarrow,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import {
  CARGO_PROBLEM_CATEGORIES,
  CARGO_PROBLEM_QUEUES,
  useCargoProblemLabels,
} from '../labels.js';
import {
  useCargoProblemAssigneeCandidates,
  useCargoProblemCandidates,
  useCreateCargoProblemWorkspace,
  useReplaceCargoProblemWorkspace,
  useTransitionCargoProblemWorkspace,
} from '../queries.js';
import { useCargoProblemAttachments } from '../useCargoProblemAttachments.js';
import { CargoProblemAttachmentField } from './CargoProblemAttachmentField.js';
import { GuardedCloseAction, GuardedProblemForm } from './CargoProblemEditorShell.js';
import type { CargoProblemDraftCargo } from './CargoProblemsListPage.js';

export function CargoProblemCreateDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { cargoProblemDraftCargo?: CargoProblemDraftCargo } | null;
  return (
    <CargoProblemEditor
      {...(state?.cargoProblemDraftCargo ? { initialCargo: state.cargoProblemDraftCargo } : {})}
      onClose={() => navigate('/cargo/problems')}
    />
  );
}

export function CargoProblemEditor({
  problem,
  initialCargo,
  onClose,
}: {
  problem?: CargoProblemDetail;
  initialCargo?: CargoProblemDraftCargo;
  onClose: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const navigate = useNavigate();
  const narrow = useNarrow();
  const showToast = useToastStore((state) => state.show);
  const create = useCreateCargoProblemWorkspace();
  const replace = useReplaceCargoProblemWorkspace();
  const transition = useTransitionCargoProblemWorkspace();
  const attachments = useCargoProblemAttachments();
  const [initialSelection] = useState(() => toDraftCargo(initialCargo ?? problem?.cargo));
  const [cargoSearch, setCargoSearch] = useState(initialSelection?.article ?? '');
  const [cargoPickerActive, setCargoPickerActive] = useState(false);
  const [cargo, setCargo] = useState<CargoProblemDraftCargo | undefined>(initialSelection);
  const [draftCargo, setDraftCargo] = useState<CargoProblemDraftCargo | undefined>();
  const [submitExistingDraft] = useState(
    () => problem?.status === 'DRAFT' && problem.assignee === null,
  );
  const [selectedAssignee, setSelectedAssignee] = useState<CargoProblemActor | null>(
    problem?.assignee ?? null,
  );
  const [draftId, setDraftId] = useState<string | null>(null);
  const [publishFailed, setPublishFailed] = useState(false);
  const candidates = useCargoProblemCandidates(cargoSearch, cargoPickerActive);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<CreateCargoProblemWorkspaceInput>({
    resolver: zodResolver(CreateCargoProblemWorkspaceInputSchema),
    mode: 'onTouched',
    defaultValues: {
      cargoArticleId: initialSelection?.id ?? problem?.cargo?.id ?? '',
      category: problem?.category ?? 'SUPPLIER',
      queue: problem?.queue ?? 'CHINA_LOGISTICS',
      description: problem?.description ?? '',
      assigneeId: problem?.assignee?.id ?? '',
    },
  });
  const queue = watch('queue');
  const assigneeId = watch('assigneeId');
  const assigneeQuery = useCargoProblemAssigneeCandidates(queue);
  const assigneeRows = assigneeQuery.data?.candidates ?? [];
  const assigneeCandidatesSettled = assigneeQuery.isSuccess && !assigneeQuery.isFetching;
  const assigneeEligible =
    assigneeCandidatesSettled && assigneeRows.some((item) => item.id === assigneeId);
  const assigneeUnavailable = Boolean(assigneeId && assigneeCandidatesSettled && !assigneeEligible);
  const cargoOptions = useMemo(() => {
    const rows = [...(candidates.data?.items ?? [])];
    if (cargo && !rows.some((item) => item.id === cargo.id)) rows.unshift(cargo);
    return rows.map((item) => ({ value: item.id, label: cargoLabel(item) }));
  }, [candidates.data?.items, cargo]);
  const assigneeOptions = useMemo(() => {
    const rows = [...assigneeRows];
    if (selectedAssignee && !rows.some((item) => item.id === selectedAssignee.id)) {
      rows.unshift({
        ...selectedAssignee,
        name: assigneeUnavailable
          ? `${selectedAssignee.name} · ${t('editor.assigneeUnavailable')}`
          : selectedAssignee.name,
      });
    }
    return rows.map((item) => ({ value: item.id, label: item.name }));
  }, [assigneeRows, assigneeUnavailable, selectedAssignee, t]);
  const busy = create.isPending || replace.isPending || transition.isPending || attachments.busy;
  const dirty =
    isDirty ||
    cargo?.id !== initialSelection?.id ||
    attachments.items.length > 0 ||
    draftId !== null;

  async function submit(values: CreateCargoProblemWorkspaceInput, bypassNext: () => void) {
    setPublishFailed(false);
    try {
      if (problem) {
        await replace.mutateAsync({ id: problem.id, input: values });
        if (problem.status === 'REJECTED' || submitExistingDraft) {
          await attachments.commitWithRecovery(problem.id, 'workflow', (files) =>
            transition.mutateAsync({
              id: problem.id,
              input: { action: 'submit', attachments: files },
            }),
          );
          showToast({
            tone: 'success',
            text: problem.status === 'REJECTED' ? t('toasts.resubmitted') : t('toasts.published'),
          });
        } else {
          showToast({ tone: 'success', text: t('toasts.saved') });
        }
        reset(values);
        attachments.clear();
        onClose();
        return;
      }

      let id = draftId;
      if (id) await replace.mutateAsync({ id, input: values });
      else {
        const created = await create.mutateAsync(values);
        id = created.problem.id;
        const persistedCargo = toDraftCargo(created.problem.cargo) ?? cargo;
        setDraftId(id);
        setDraftCargo(persistedCargo);
        if (persistedCargo) {
          setCargo(persistedCargo);
          setValue('cargoArticleId', persistedCargo.id, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
      await attachments.commitWithRecovery(id, 'brief', (files) =>
        transition.mutateAsync({ id, input: { action: 'submit', attachments: files } }),
      );
      reset(values);
      attachments.clear();
      bypassNext();
      showToast({ tone: 'success', text: t('toasts.published') });
      navigate(`/cargo/problems/${id}`, { replace: true });
    } catch (error) {
      setPublishFailed(true);
      showToast({ tone: 'error', text: errorMessage(error) });
    }
  }

  return (
    <EditorDrawer dirty={dirty} busy={busy} onConfirmClose={onClose} width={680} narrow={narrow}>
      <GuardedProblemForm handleSubmit={handleSubmit} submit={submit}>
        <header>
          <div>
            <span>{problem ? t('editor.editEyebrow') : t('editor.createEyebrow')}</span>
            <h2>{problem?.publicCode ?? t('editor.newTitle')}</h2>
          </div>
          <GuardedCloseAction iconOnly label={t('actions.close')} onClose={onClose} />
        </header>
        <fieldset className="cargo-problem-editor-body" disabled={busy}>
          {draftId ? (
            <Banner tone={publishFailed ? 'warn' : 'info'} icon="shield">
              {publishFailed ? t('editor.privateDraftRetry') : t('editor.privateDraft')}
            </Banner>
          ) : null}
          <SectionCard title={t('editor.contextSection')}>
            <FormRow
              label={t('editor.cargo')}
              error={errors.cargoArticleId ? t('validation.cargoRequired') : undefined}
            >
              {problem || draftId ? (
                <div className="cargo-problem-selected-cargo">
                  {cargoLabel(draftCargo ?? cargo) || t('cargoUnavailable')}
                </div>
              ) : (
                <Controller
                  control={control}
                  name="cargoArticleId"
                  render={({ field }) => (
                    <Combobox
                      value={field.value || null}
                      disabled={busy}
                      loading={candidates.isFetching}
                      options={cargoOptions}
                      placeholder={t('editor.cargoSearch')}
                      ariaLabel={t('editor.cargo')}
                      onQueryChange={(value) => {
                        setCargoPickerActive(true);
                        setCargoSearch(value);
                      }}
                      onChange={(id) => {
                        const selected = candidates.data?.items.find((item) => item.id === id);
                        if (selected) setCargo(selected);
                        field.onChange(id);
                      }}
                    />
                  )}
                />
              )}
            </FormRow>
          </SectionCard>
          <SectionCard title={t('editor.briefSection')}>
            <FormRow
              label={t('editor.category')}
              error={errors.category ? t('validation.categoryRequired') : undefined}
            >
              <Controller
                control={control}
                name="category"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(value) => field.onChange(value as CargoProblemCategory)}
                    options={CARGO_PROBLEM_CATEGORIES.map((value) => ({
                      value,
                      label: labels.category(value),
                    }))}
                  />
                )}
              />
            </FormRow>
            <FormRow
              label={t('editor.description')}
              error={errors.description ? t('validation.descriptionRequired') : undefined}
            >
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <Textarea
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel={t('editor.description')}
                    rows={6}
                  />
                )}
              />
            </FormRow>
          </SectionCard>
          <SectionCard title={t('editor.ownershipSection')}>
            <div className="cargo-problem-editor-grid">
              <FormRow
                label={t('editor.queue')}
                error={errors.queue ? t('validation.queueRequired') : undefined}
              >
                <Controller
                  control={control}
                  name="queue"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value as CargoProblemQueue);
                        setValue('assigneeId', '', { shouldDirty: true, shouldValidate: true });
                        setSelectedAssignee(null);
                      }}
                      options={CARGO_PROBLEM_QUEUES.map((value) => ({
                        value,
                        label: labels.queue(value),
                      }))}
                    />
                  )}
                />
              </FormRow>
              <FormRow
                label={t('editor.assignee')}
                error={errors.assigneeId ? t('validation.assigneeRequired') : undefined}
              >
                <Controller
                  control={control}
                  name="assigneeId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      disabled={assigneeQuery.isFetching || assigneeQuery.isError}
                      placeholder={t('editor.assigneePlaceholder')}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedAssignee(
                          assigneeRows.find((candidate) => candidate.id === value) ?? null,
                        );
                      }}
                      options={assigneeOptions}
                    />
                  )}
                />
              </FormRow>
            </div>
            {assigneeQuery.isError ? (
              <Banner tone="error">{t('reassign.loadError')}</Banner>
            ) : assigneeUnavailable ? (
              <Banner tone="warn">{t('editor.assigneeUnavailableHelp')}</Banner>
            ) : null}
          </SectionCard>
          {!problem || problem.status === 'REJECTED' ? (
            <SectionCard title={t('editor.attachmentsSection')}>
              <CargoProblemAttachmentField
                items={attachments.items}
                busy={busy}
                onAdd={attachments.add}
                onRemove={attachments.remove}
              />
            </SectionCard>
          ) : null}
        </fieldset>
        <footer>
          <GuardedCloseAction label={t('actions.cancel')} onClose={onClose} />
          <Button
            type="submit"
            kind="primary"
            disabled={busy || assigneeQuery.isError || Boolean(assigneeId && !assigneeEligible)}
          >
            {problem?.status === 'REJECTED'
              ? t('actions.editAndResubmit')
              : submitExistingDraft
                ? t('actions.submit')
                : problem
                  ? t('actions.save')
                  : draftId
                    ? t('actions.retryPublish')
                    : t('actions.publish')}
          </Button>
        </footer>
      </GuardedProblemForm>
    </EditorDrawer>
  );
}

function toDraftCargo(
  cargo: CargoProblemDraftCargo | CargoProblemDetail['cargo'] | undefined,
): CargoProblemDraftCargo | undefined {
  if (!cargo) return undefined;
  return { ...cargo, article: cargo.article ?? '' };
}

function cargoLabel(cargo?: CargoProblemDraftCargo): string {
  if (!cargo) return '';
  return [cargo.article, cargo.nameGoods].filter(Boolean).join(' · ');
}
